import { useCallback, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import Toggle from "ds/components/Toggle";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import ButtonIcon from "ds/components/ButtonIcon";
import LoadingIndicator from "ds/components/LoadingIndicator";
import { ChevronRight } from "components/icons/generated";
import Box from "ds/components/Box";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";

import { SelectAllOption } from "../../types";
import { TableSelectionStore } from "../../store/useTableSelectionStore/types";
import {
  selectIsFullySelected,
  selectIsIndeterminate,
} from "../../store/useTableSelectionStore/selectors";

type TableCellContentSelectionProps = {
  selectionStore: TableSelectionStore;
  selectionOptions: SelectAllOption[];
};

const TableHeadInteractiveColumnSelection = ({
  selectionStore,
  selectionOptions,
}: TableCellContentSelectionProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { onError } = useTypedContext(FlashContext);

  const { isSelected, isIndeterminate, clearSelected, loading } = selectionStore(
    useShallow((state) => ({
      isSelected: selectIsFullySelected(state),
      isIndeterminate: selectIsIndeterminate(state),
      clearSelected: state.clearSelected,
      loading: state.loading,
    }))
  );

  const handleOptionSelect = useCallback(
    async (option: SelectAllOption) => {
      try {
        await option.onSelect();
      } catch (error) {
        onError(error);
      }
    },
    [onError]
  );

  const handleToggleChange = useCallback(() => {
    if (isIndeterminate || isSelected) {
      clearSelected();
    } else {
      handleOptionSelect(selectionOptions[0]);
    }
  }, [clearSelected, handleOptionSelect, isIndeterminate, isSelected, selectionOptions]);

  const getTooltip = useCallback(
    (isSelected: boolean) => {
      if (isSelected) {
        return "Unselect items";
      }
      return selectionOptions[0]?.title || "Select all items";
    },
    [selectionOptions]
  );

  if (selectionOptions.length === 0) {
    return null;
  }

  return (
    <Box align="center" gap="small">
      <Toggle
        isSelected={isSelected}
        isIndeterminate={isIndeterminate}
        variant="checkbox"
        onChange={handleToggleChange}
        tooltip={getTooltip}
        isDisabled={loading}
      />
      {selectionOptions.length > 1 && (
        <LoadingIndicator loading={loading}>
          <DropdownMenu
            onOpenChange={setIsDropdownOpen}
            isOpen={isDropdownOpen}
            triggerComponent={
              <ButtonIcon
                iconRotate={isDropdownOpen ? "270" : "90"}
                icon={ChevronRight}
                variant="ghost"
              >
                Select items
              </ButtonIcon>
            }
          >
            {selectionOptions.map((option) => (
              <DropdownMenuItem key={option.title} onAction={() => handleOptionSelect(option)}>
                {option.title}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </LoadingIndicator>
      )}
    </Box>
  );
};

TableHeadInteractiveColumnSelection.displayName = "DS.Table.Head.InteractiveColumn.Selection";

export default TableHeadInteractiveColumnSelection;
